import postAdditionalInfo from "@faire/web-api/api/brand-lead/additional-info/post";
import { IBrandLeadApplicationAdditionalInfo } from "@faire/web-api/indigofair/data/IBrandLeadApplicationAdditionalInfo";
import { ICreateBrandLeadApplicationAdditionalInfoRequest } from "@faire/web-api/indigofair/data/ICreateBrandLeadApplicationAdditionalInfoRequest";
import { getWebApiErrorMessage } from "@faire/web/common/getErrorMessage";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { logError } from "@faire/web/common/logging";
import { makeObservable } from "@faire/web/common/makeObservable";
import { NumberParser } from "@faire/web/common/NumberParser";
import { trackEvent } from "@faire/web/common/trackEvent";
import { IOption } from "@faire/web/slate/Input/Dropdown";
import { observable } from "mobx";

export class MakerSignUpAdditionalQuestionsViewState {
  @observable
  yearEstablished?: number;

  @observable
  instagramHandle?: string;

  @observable
  faireValues: Set<string> = new Set();

  @observable
  additionalNotes?: string;

  @observable
  formSubmitted: boolean = false;

  @observable
  errors: Record<string, string> = {};

  constructor(
    private brandLeadToken: string,
    private strictLocalize: StrictLocalizeFunction
  ) {
    makeObservable(this);
  }

  handleYearEstablishedChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numberParser = new NumberParser();
    const value = numberParser.parse(e.target.value);

    if (Number.isNaN(value) && e.target.value) {
      this.errors.yearEstablished = this.strictLocalize({
        defaultMessage: "Please enter a number",
      });
      this.yearEstablished = undefined;
    } else {
      this.yearEstablished = value;
      if (this.errors.yearEstablished) {
        delete this.errors.yearEstablished;
      }
    }
  };

  handleInstagramHandleChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Using this regex for validating Instagram handles: https://regexr.com/3cg7r
    const regex = /^(?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}$/;
    if (!e.target.value.match(regex)) {
      this.errors.instagramHandle = this.strictLocalize({
        defaultMessage: "Please enter a valid instagram handle",
      });
      this.instagramHandle = undefined;
    } else {
      this.instagramHandle = e.target.value;
      if (this.errors.instagramHandle) {
        delete this.errors.instagramHandle;
      }
    }
  };

  handleAdditionalNotesChanged = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    this.additionalNotes = e.target.value;
  };

  onChangeFaireValue = (val: IOption) => {
    if (this.isFaireValueSelected(val)) {
      this.faireValues.delete(val.value);
    } else {
      this.faireValues.add(val.value);
    }
  };

  isFaireValueSelected = (val: IOption): boolean => {
    return this.faireValues.has(val.value);
  };

  private verifyForm = () => {
    if (this.errors.submission) {
      delete this.errors.submission;
    }

    if (Object.values(this.errors).length) {
      this.errors.submission = this.strictLocalize({
        defaultMessage: "Please address all values in red.",
      });

      throw new Error("Invalid form");
    }
  };

  onSave = async () => {
    this.verifyForm();

    const request = ICreateBrandLeadApplicationAdditionalInfoRequest.build({
      additional_info: IBrandLeadApplicationAdditionalInfo.build({
        brand_lead_token: this.brandLeadToken,
        year_established: this.yearEstablished,
        instagram_handle: this.instagramHandle,
        faire_values: Array.from(this.faireValues),
        additional_notes: this.additionalNotes,
      }),
    });

    try {
      await postAdditionalInfo(request);
      trackEvent("maker-application/tell-us-more/submit");
      this.formSubmitted = true;
    } catch (e) {
      const fallbackMessage = this.strictLocalize({
        defaultMessage:
          "Something went wrong. Please try again or visit our Help Center if you have any questions.",
        description: { text: "Notifying users of an error." },
      });
      this.errors.submission = getWebApiErrorMessage(
        e,
        this.strictLocalize,
        fallbackMessage
      );

      logError(e);
      // Re-throwing the error to cause the button to shake
      throw e;
    }
  };
}
